/**
 * Check Entry Form Component
 *
 * Form for recording incoming check payments from buyers with
 * optional allocation to shipments and check image upload.
 */

import React, { useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Modal,
  Space,
  Typography,
  Divider,
  message,
  AutoComplete,
  Spin,
  Card,
  DatePicker,
  Upload,
} from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import {
  PlusOutlined,
  DollarOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  ArrowDownOutlined,
  UploadOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bankTransactionsApi } from '../../services/api';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

interface ShipmentSearchResult {
  id: string;
  reference: string;
  contract?: {
    reference: string;
    buyer?: { id: string; name: string; shortName: string };
    supplier?: { id: string; name: string; shortName: string };
  };
}

interface Buyer {
  id: string;
  name: string;
  shortName?: string;
}

interface CheckEntryFormProps {
  /** Open modal with pre-selected shipment */
  shipmentId?: string;
  /** Called when check is successfully created */
  onSuccess?: (check: any) => void;
  /** Render as a button that opens modal, or inline form */
  mode?: 'button' | 'inline';
}

const CheckEntryForm: React.FC<CheckEntryFormProps> = ({
  shipmentId: initialShipmentId,
  onSuccess,
  mode = 'button',
}) => {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [shipmentSearch, setShipmentSearch] = useState('');
  const [selectedShipment, setSelectedShipment] = useState<ShipmentSearchResult | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const queryClient = useQueryClient();

  const { data: buyersData } = useQuery({
    queryKey: ['companies', 'buyers'],
    queryFn: async () => {
      const response = await api.get('/companies', {
        params: { type: 'BUYER', limit: 200 },
      });
      return (response as any).companies || [];
    },
  });

  const buyers: Buyer[] = buyersData || [];

  const { data: shipmentResults, isLoading: searchingShipments } = useQuery({
    queryKey: ['shipment-search', shipmentSearch],
    queryFn: async () => {
      if (!shipmentSearch || shipmentSearch.length < 2) return [];
      const searchTerm = shipmentSearch.replace(/(\.\d{2})$/, '');
      const response = await api.get('/contracts', {
        params: { search: searchTerm, limit: 10, include: 'shipments' },
      });
      const shipments: ShipmentSearchResult[] = [];
      for (const contract of (response as any).contracts || []) {
        for (const shipment of contract.shipments || []) {
          shipments.push({
            id: shipment.id,
            reference: shipment.reference || `${contract.reference}-${shipment.id.slice(-4)}`,
            contract: {
              reference: contract.reference,
              buyer: contract.buyer,
              supplier: contract.supplier,
            },
          });
        }
      }
      return shipments;
    },
    enabled: shipmentSearch.length >= 2,
  });

  const createCheckMutation = useMutation({
    mutationFn: async (data: any) => {
      const transaction = await bankTransactionsApi.create({
        transactionType: 'CHECK_IN',
        amount: data.amount,
        date: data.date,
        reference: data.checkNumber,
        counterparty: data.payer,
        description: data.description,
        sourceType: 'manual_entry',
        currencyCode: 'USD',
      });

      const txnData = (transaction as any)?.data || transaction;
      const transactionId = txnData?.id || txnData?.transaction?.id;

      if (data.shipmentId && transactionId) {
        try {
          await bankTransactionsApi.addAllocation(transactionId, {
            shipmentId: data.shipmentId,
            amount: data.amount,
            allocationType: 'INVOICE_PAYMENT',
          });
        } catch (allocError) {
          console.warn('Allocation failed but transaction was created:', allocError);
        }
      }

      return transaction;
    },
    onSuccess: (response: any) => {
      message.success('Check payment recorded successfully');
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['shipment-transactions'] });
      form.resetFields();
      setSelectedShipment(null);
      setShipmentSearch('');
      setFileList([]);
      setModalVisible(false);
      onSuccess?.(response);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to record check payment');
    },
  });

  const handleShipmentSelect = (shipment: ShipmentSearchResult) => {
    setSelectedShipment(shipment);
    setShipmentSearch(shipment.reference);

    if (shipment.contract?.buyer) {
      const buyer = shipment.contract.buyer;
      form.setFieldsValue({
        payerId: buyer.id,
      });
    }
  };

  const clearShipment = () => {
    setSelectedShipment(null);
    setShipmentSearch('');
  };

  const handleUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleSubmit = async (values: any) => {
    const selectedBuyer = buyers.find((b) => b.id === values.payerId);
    const payerName = selectedBuyer?.shortName || selectedBuyer?.name || values.payerId;

    const checkData = {
      amount: values.amount,
      date: values.date ? values.date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      checkNumber: values.checkNumber,
      payer: payerName,
      shipmentId: selectedShipment?.id,
      description: values.description,
    };

    createCheckMutation.mutate(checkData);
  };

  const formContent = (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        date: dayjs(),
      }}
    >
      <Form.Item
        label="Check Number"
        name="checkNumber"
        rules={[{ required: true, message: 'Please enter the check number' }]}
        help="The check number printed on the check"
      >
        <Input placeholder="e.g., 1234" />
      </Form.Item>

      <Space size="middle" style={{ display: 'flex', marginBottom: 0 }}>
        <Form.Item
          label="Amount (USD)"
          name="amount"
          rules={[
            { required: true, message: 'Please enter amount' },
            { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
          ]}
          style={{ flex: 1 }}
        >
          <InputNumber
            prefix={<DollarOutlined />}
            style={{ width: '100%' }}
            precision={2}
            min={0.01}
            placeholder="0.00"
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => value!.replace(/,/g, '') as any}
          />
        </Form.Item>

        <Form.Item
          label="Check Date"
          name="date"
          rules={[{ required: true, message: 'Please select date' }]}
          style={{ flex: 1 }}
        >
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
      </Space>

      <Form.Item
        label="Received From (Buyer)"
        name="payerId"
        rules={[{ required: true, message: 'Please select the payer' }]}
        help="Select the buyer who issued the check"
      >
        <Select
          showSearch
          placeholder="Select buyer..."
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
          }
          options={buyers.map((buyer) => ({
            value: buyer.id,
            label: buyer.shortName || buyer.name,
          }))}
        />
      </Form.Item>

      <Divider />

      <Form.Item
        label="Allocate to Shipment (Optional)"
        help="Search by contract or shipment reference to allocate this check"
      >
        <AutoComplete
          value={shipmentSearch}
          onChange={setShipmentSearch}
          options={(shipmentResults || []).map((s) => ({
            value: s.reference,
            label: (
              <div>
                <Text strong>{s.reference}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {s.contract?.reference} | {s.contract?.buyer?.shortName || s.contract?.buyer?.name} / {s.contract?.supplier?.shortName || s.contract?.supplier?.name}
                </Text>
              </div>
            ),
            shipment: s,
          }))}
          onSelect={(_, option: any) => handleShipmentSelect(option.shipment)}
          placeholder="Search shipment..."
          style={{ width: '100%' }}
          notFoundContent={
            searchingShipments ? <Spin size="small" /> :
            shipmentSearch.length < 2 ? 'Type at least 2 characters' : 'No shipments found'
          }
        >
          <Input prefix={<SearchOutlined />} />
        </AutoComplete>
      </Form.Item>

      {selectedShipment && (
        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space direction="vertical" size="small">
              <div>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                <Text strong>Allocated to: {selectedShipment.reference}</Text>
              </div>
              <Text type="secondary">
                Contract: {selectedShipment.contract?.reference} |
                Buyer: {selectedShipment.contract?.buyer?.shortName || selectedShipment.contract?.buyer?.name || 'N/A'} |
                Supplier: {selectedShipment.contract?.supplier?.shortName || selectedShipment.contract?.supplier?.name || 'N/A'}
              </Text>
            </Space>
            <Button size="small" onClick={clearShipment}>Clear</Button>
          </Space>
        </Card>
      )}

      <Form.Item
        label="Memo / Notes"
        name="description"
        help="Any additional notes about this check"
      >
        <TextArea rows={2} placeholder="Optional notes..." />
      </Form.Item>

      <Divider />

      <Form.Item
        label="Check Image (Optional)"
        help="Upload a scan or photo of the check for records"
      >
        <Upload
          listType="picture-card"
          fileList={fileList}
          onChange={handleUploadChange}
          beforeUpload={() => false}
          maxCount={2}
          accept="image/*,.pdf"
        >
          {fileList.length < 2 && (
            <div>
              <FileImageOutlined style={{ fontSize: 24 }} />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          )}
        </Upload>
      </Form.Item>

      {mode === 'inline' && (
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={createCheckMutation.isPending}
            icon={<PlusOutlined />}
          >
            Record Check Payment
          </Button>
        </Form.Item>
      )}
    </Form>
  );

  if (mode === 'inline') {
    return formContent;
  }

  return (
    <>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setModalVisible(true)}
      >
        Record Check
      </Button>

      <Modal
        title={
          <Space>
            <ArrowDownOutlined style={{ color: '#52c41a' }} />
            Record Check Payment
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setSelectedShipment(null);
          setShipmentSearch('');
          setFileList([]);
        }}
        onOk={() => form.submit()}
        confirmLoading={createCheckMutation.isPending}
        width={600}
        okText="Record Check"
      >
        {formContent}
      </Modal>
    </>
  );
};

export default CheckEntryForm;
